import { auswahlAusSchluessel } from '../../services/mangelKatalog/auswahl';
import { seiteSchneiden } from '../../services/mangelKatalog/seiten';
import type { SuchTreffer } from '../../services/mangelKatalog/suche';
import type {
    AufgeloesterKatalog,
    AufgeloesterKnoten,
    Gesamtkatalog,
    KatalogAuswahl,
    KatalogSprache,
    MerkSchluessel,
} from '../../services/mangelKatalog/typen';
import type { RadialStueck } from './RadialMenu';
import { katalogTexte } from './texte';

/**
 * Reine Ableitungen der Katalogauswahl („Standardmangel per Torte“, Paket D):
 * was die aktuelle Ebene/Seite zeigt (Tortenstücke bzw. Kacheln nach der
 * Seitenregel 8 + „Sonstiges“), was ein Tipp auf Position n auslöst, wie
 * Pfeiltasten den Fokus bewegen und wie Tasten im Dialog gedeutet werden.
 * Die Komponenten rechnen nichts davon selbst.
 */

/** ID des „Sonstiges“-Stücks (blättert weiter). */
export const WEITER_ID = '__weiter';
/** Plätze je Seite: 8 Einträge + Platz 9 („Sonstiges“ bzw. Freitext/leer). */
export const PLAETZE = 9;

// ─── Beschriftung ────────────────────────────────────────────────────────────

/** Screenreader-Text: Silbentrenner '-\n' fällt weg, sonst wird '\n' ein Leerzeichen. */
export function ariaBeschriftung(text: string): string {
    if (typeof text !== 'string') return '';
    return text.replace(/-\n/g, '').replace(/\s*\n\s*/g, ' ').trim();
}

const kuerzen = (z: string, max: number, erzwingen = false): string => {
    if (z.length <= max && !erzwingen) return z;
    const kurz = z.length <= max - 1 ? z : z.slice(0, Math.max(1, max - 1)).trimEnd();
    return `${kurz}…`;
};

/** Wortweise auf Zeilen zu höchstens `max` Zeichen (ein zu langes Wort bleibt ganz). */
function umbrechen(text: string, max: number): string[] {
    const zeilen: string[] = [];
    let zeile = '';
    for (const wort of text.split(' ')) {
        if (!wort) continue;
        if (!zeile) zeile = wort;
        else if (zeile.length + 1 + wort.length <= max) zeile += ` ${wort}`;
        else {
            zeilen.push(zeile);
            zeile = wort;
        }
    }
    if (zeile) zeilen.push(zeile);
    return zeilen;
}

/**
 * Zeilen der Tortenbeschriftung, höchstens zwei. Vorgegebene Umbrüche ('\n')
 * gelten; eine einzeilige Beschriftung (z. B. Kurztext eines eigenen Mangels)
 * wird wortweise umbrochen. Zu Langes endet mit „…“ (der volle Text steht im
 * Screenreader-Namen).
 */
export function tortenZeilen(text: string, max = 12): string[] {
    if (typeof text !== 'string') return [];
    const vorgegeben = text
        .split('\n')
        .map((z) => z.replace(/\s+/g, ' ').trim())
        .filter(Boolean);
    if (vorgegeben.length === 0) return [];
    const zeilen = vorgegeben.length > 1 ? vorgegeben : umbrechen(vorgegeben[0], max);
    if (zeilen.length <= 2) return zeilen.map((z) => kuerzen(z, max));
    return [kuerzen(zeilen[0], max), kuerzen(zeilen.slice(1).join(' '), max, true)];
}

// ─── Pfad ────────────────────────────────────────────────────────────────────

/**
 * Längster gültiger Anfang des Pfads: jede ID ist ein sichtbares Kind der
 * vorigen (die erste eine Kategorie der Wurzel) und kein Mangel.
 */
export function pfadBereinigen(katalog: AufgeloesterKatalog, pfad: readonly string[] | null | undefined): string[] {
    const aus: string[] = [];
    let eltern = '';
    for (const id of Array.isArray(pfad) ? pfad : []) {
        if (typeof id !== 'string' || !id) break;
        const k = katalog.knoten.get(id);
        if (!k || k.typ === 'mangel' || !(katalog.kinder.get(eltern) ?? []).includes(id)) break;
        aus.push(id);
        eltern = id;
    }
    return aus;
}

/**
 * Startpfad für „Ändern“ (Chip im Schritt „Details“, Knopf „Standard“ an der
 * Karte): die Ebene, in der der gewählte Mangel steht; sonst die Kategorie;
 * sonst die Wurzel.
 */
export function startPfadVon(
    katalog: AufgeloesterKatalog,
    felder: { katalogKey?: string | null; katalogKategorie?: string | null },
): string[] {
    const key = felder?.katalogKey;
    const k = key ? katalog.knoten.get(key) : undefined;
    if (k) {
        const kette: string[] = [];
        const besucht = new Set<string>();
        let id: string | null = k.typ === 'mangel' ? k.eltern : k.id;
        while (id && !besucht.has(id)) {
            besucht.add(id);
            kette.unshift(id);
            id = katalog.knoten.get(id)?.eltern ?? null;
        }
        const pfad = pfadBereinigen(katalog, kette);
        if (pfad.length) return pfad;
    }
    const kategorie = felder?.katalogKategorie;
    return kategorie ? pfadBereinigen(katalog, [kategorie]) : [];
}

// ─── Ansicht der aktuellen Ebene ─────────────────────────────────────────────

export interface TortenAnsicht {
    art: 'torte';
    /** 1 = Kategorien, 2 = Unterpunkte einer Kategorie. */
    ebene: 1 | 2;
    /** Bereinigter Pfad (IDs). */
    pfad: string[];
    /** Namen entlang des Pfads. */
    namen: string[];
    titel: string;
    /** Genau 9. */
    stuecke: RadialStueck[];
    /** Geklemmte Seite (0-basiert). */
    seite: number;
    seiten: number;
    mitteBeschriftung: string;
    mitteAriaLabel: string;
}

export type KachelPlatz =
    | { art: 'mangel'; knoten: AufgeloesterKnoten }
    | { art: 'gruppe'; knoten: AufgeloesterKnoten }
    | { art: 'weiter'; rest: number }
    | { art: 'freitext'; kategorie: AufgeloesterKnoten }
    | { art: 'leer' };

export interface KachelAnsicht {
    art: 'kacheln';
    pfad: string[];
    namen: string[];
    /** „Absturz › Seitenschutz“. */
    titel: string;
    /** Genau 9. */
    plaetze: KachelPlatz[];
    seite: number;
    seiten: number;
}

export type KatalogAnsicht = TortenAnsicht | KachelAnsicht;

const sichtbareKinder = (katalog: AufgeloesterKatalog, eltern: string): AufgeloesterKnoten[] =>
    (katalog.kinder.get(eltern) ?? [])
        .map((id) => katalog.knoten.get(id))
        .filter((k): k is AufgeloesterKnoten => !!k);

/**
 * Was die Ebene zeigt: Pfad [] → Torte der Kategorien, [kat] → Torte der
 * Unterpunkte, ab zwei Ebenen → Kachelraster. Seitenregel: 8 Einträge, an
 * Platz 9 „Sonstiges“, solange weitere folgen; in der letzten Kachelseite
 * steht dort „Freitext in <Kategorie>“. Leere Plätze bleiben leer, damit die
 * Positionen fest sind. Feste Beschriftungen in der Katalogsprache (texte.ts).
 */
export function ansichtBerechnen(
    katalog: AufgeloesterKatalog,
    pfadRoh: readonly string[],
    seiteRoh: number,
    sprache: KatalogSprache = 'de',
): KatalogAnsicht {
    const t = katalogTexte(sprache);
    const pfad = pfadBereinigen(katalog, pfadRoh);
    const knoten = pfad.map((id) => katalog.knoten.get(id)).filter((k): k is AufgeloesterKnoten => !!k);
    const namen = knoten.map((k) => k.name);
    const kinder = sichtbareKinder(katalog, pfad.length ? pfad[pfad.length - 1] : '');
    const s = seiteSchneiden(kinder, seiteRoh);

    if (pfad.length < 2) {
        const ebene: 1 | 2 = pfad.length === 0 ? 1 : 2;
        const stuecke: RadialStueck[] = Array.from({ length: PLAETZE }, (_, i): RadialStueck => {
            const k = i < 8 ? s.eintraege[i] : undefined;
            if (k) return { id: k.id, beschriftung: k.kurz || k.name, pikto: k.pikto, art: 'eintrag' };
            if (i === 8 && s.weiter) return { id: WEITER_ID, beschriftung: t.sonstiges, art: 'weiter', zusatz: String(s.rest) };
            return { id: `__leer${i}`, beschriftung: '', art: 'leer' };
        });
        const ersteSeite = s.index === 0;
        return {
            art: 'torte',
            ebene,
            pfad,
            namen,
            titel: ebene === 1 ? t.themaWaehlen : namen[0],
            stuecke,
            seite: s.index,
            seiten: s.anzahl,
            mitteBeschriftung: ersteSeite ? t.zurueck : t.seiteZurueck,
            mitteAriaLabel: !ersteSeite
                ? t.vorigeSeite(s.index, s.anzahl)
                : ebene === 1
                    ? t.zurueck
                    : t.zurueckZurThemenauswahl,
        };
    }

    const kategorie = knoten[0];
    const plaetze: KachelPlatz[] = Array.from({ length: PLAETZE }, (_, i): KachelPlatz => {
        const k = i < 8 ? s.eintraege[i] : undefined;
        if (k) return k.typ === 'mangel' ? { art: 'mangel', knoten: k } : { art: 'gruppe', knoten: k };
        if (i === 8) {
            if (s.weiter) return { art: 'weiter', rest: s.rest };
            if (kategorie) return { art: 'freitext', kategorie };
        }
        return { art: 'leer' };
    });
    return {
        art: 'kacheln',
        pfad,
        namen,
        titel: namen.join(' › '),
        plaetze,
        seite: s.index,
        seiten: s.anzahl,
    };
}

// ─── Aktionen ────────────────────────────────────────────────────────────────

export type PlatzAktion =
    | { typ: 'oeffnen'; id: string }
    | { typ: 'waehlen'; knoten: AufgeloesterKnoten }
    | { typ: 'weiter' }
    | { typ: 'freitext'; kategorie: AufgeloesterKnoten };

/** Tipp auf einen Knoten: ein Mangel wird gewählt, alles andere geöffnet. */
export function knotenAktion(katalog: AufgeloesterKatalog, id: string): PlatzAktion | null {
    const k = katalog.knoten.get(id);
    if (!k) return null;
    if (k.typ === 'mangel') return k.vorlage ? { typ: 'waehlen', knoten: k } : null;
    return { typ: 'oeffnen', id: k.id };
}

/** Was Platz `index` (0 … 8) der Ansicht auslöst; leer → null. */
export function platzAktion(ansicht: KatalogAnsicht, katalog: AufgeloesterKatalog, index: number): PlatzAktion | null {
    if (!Number.isInteger(index) || index < 0 || index >= PLAETZE) return null;
    if (ansicht.art === 'torte') {
        const s = ansicht.stuecke[index];
        if (!s || s.art === 'leer') return null;
        if (s.art === 'weiter') return { typ: 'weiter' };
        return knotenAktion(katalog, s.id);
    }
    const p = ansicht.plaetze[index];
    switch (p?.art) {
        case 'mangel':
            return p.knoten.vorlage ? { typ: 'waehlen', knoten: p.knoten } : null;
        case 'gruppe':
            return { typ: 'oeffnen', id: p.knoten.id };
        case 'weiter':
            return { typ: 'weiter' };
        case 'freitext':
            return { typ: 'freitext', kategorie: p.kategorie };
        default:
            return null;
    }
}

// ─── Tastatur ────────────────────────────────────────────────────────────────

const RASTER_SCHRITT: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 3, ArrowUp: -3 };

/**
 * Nächster fokussierbarer Platz für eine Pfeiltaste (Pos1/Ende: erster/letzter).
 * 'ring': Rechts/Unten im Uhrzeigersinn, Links/Oben dagegen, mit Umlauf.
 * 'raster' (3 Spalten): Rechts/Links ±1, Unten/Oben ±3, ohne Umlauf.
 * Leere Plätze werden übersprungen; ohne Bewegung → null.
 */
export function naechsterPlatz(
    aktuell: number | null,
    taste: string,
    aktiv: readonly boolean[],
    form: 'ring' | 'raster',
): number | null {
    const n = aktiv.length;
    const belegt = aktiv.flatMap((a, i) => (a ? [i] : []));
    if (!belegt.length) return null;
    if (taste === 'Home') return belegt[0];
    if (taste === 'End') return belegt[belegt.length - 1];
    const gueltig = aktuell !== null && Number.isInteger(aktuell) && aktuell >= 0 && aktuell < n;

    if (form === 'ring') {
        const schritt = taste === 'ArrowRight' || taste === 'ArrowDown' ? 1 : taste === 'ArrowLeft' || taste === 'ArrowUp' ? -1 : 0;
        if (!schritt) return null;
        if (!gueltig) return schritt > 0 ? belegt[0] : belegt[belegt.length - 1];
        for (let k = 1; k < n; k++) {
            const i = (((aktuell + schritt * k) % n) + n) % n;
            if (aktiv[i]) return i;
        }
        return null;
    }

    const schritt = RASTER_SCHRITT[taste];
    if (!schritt) return null;
    if (!gueltig) return belegt[0];
    for (let i = aktuell + schritt; i >= 0 && i < n; i += schritt) if (aktiv[i]) return i;
    return null;
}

export type TastenAktion =
    | { typ: 'zurueck' }
    | { typ: 'position'; n: number }
    | { typ: 'suche'; text: string };

/**
 * Taste im Auswahl-Dialog: Esc → zurück (auch im Suchfeld); außerhalb eines
 * Eingabefelds Ziffer 1–9 → Position, ein Buchstabe → Suche mit diesem
 * Buchstaben. Mit Strg/Alt/Meta nichts (Tastenkürzel des Browsers bleiben).
 */
export function tasteDeuten(
    e: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean },
    imEingabefeld: boolean,
): TastenAktion | null {
    if (!e || typeof e.key !== 'string') return null;
    if (e.key === 'Escape' || e.key === 'Esc') return { typ: 'zurueck' };
    if (imEingabefeld || e.ctrlKey || e.metaKey || e.altKey) return null;
    if (/^[1-9]$/.test(e.key)) return { typ: 'position', n: Number(e.key) };
    if (e.key.length === 1 && /\p{L}/u.test(e.key)) return { typ: 'suche', text: e.key };
    return null;
}

// ─── Listen (Suche, Zuletzt, Favoriten) ──────────────────────────────────────

export interface ListenEintrag {
    schluessel: MerkSchluessel;
    auswahl: KatalogAuswahl;
    /** Kurztext der Kachel bzw. Überschrift des Eintrags. */
    titel: string;
    /** „Absturz › Seitenschutz“. */
    pfad: string;
}

export function listenEintrag(schluessel: MerkSchluessel, auswahl: KatalogAuswahl, katalog: AufgeloesterKatalog): ListenEintrag {
    const knoten = auswahl.art === 'knoten' && auswahl.katalogKey ? katalog.knoten.get(auswahl.katalogKey) : undefined;
    const kurz = knoten ? ariaBeschriftung(knoten.kurz) : '';
    return {
        schluessel,
        auswahl,
        titel: kurz || auswahl.vorlage?.titel || knoten?.name || '',
        pfad: auswahl.pfad.join(' › '),
    };
}

export function trefferEintraege(treffer: readonly SuchTreffer[], katalog: AufgeloesterKatalog): ListenEintrag[] {
    return treffer.map((t) => listenEintrag(t.schluessel, t.auswahl, katalog));
}

/**
 * Zuletzt/Favoriten → anzeigbare Einträge; Unauflösbares fällt weg.
 * `wartetAufQuelle`: mindestens ein Eintrag fehlt nur, weil der
 * Gesamtkatalog noch nicht geladen ist.
 */
export function merklisteAufloesen(
    schluessel: readonly MerkSchluessel[],
    katalog: AufgeloesterKatalog,
    quelle: Gesamtkatalog | null,
    zuordnung: Record<string, string>,
): { eintraege: ListenEintrag[]; wartetAufQuelle: boolean } {
    const eintraege: ListenEintrag[] = [];
    let wartetAufQuelle = false;
    const gesehen = new Set<string>();
    for (const s of schluessel ?? []) {
        if (typeof s !== 'string' || gesehen.has(s)) continue;
        gesehen.add(s);
        const a = auswahlAusSchluessel(s, katalog, quelle, zuordnung);
        if (a) eintraege.push(listenEintrag(s, a, katalog));
        else if (!quelle && s.startsWith('eintrag:')) wartetAufQuelle = true;
    }
    return { eintraege, wartetAufQuelle };
}
